import React, { useState } from 'react';
import api from '../../services/api';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';

const ThresholdConfig = ({ associationId, currentThreshold, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    enabled: currentThreshold?.enabled || false,
    thresholdType: currentThreshold?.type || 'fcfa',
    thresholdValue: currentThreshold?.value || 50000,
    resetPeriod: currentThreshold?.resetPeriod || 'monthly',
    warningPercentage: currentThreshold?.alerts?.warningPercentage || 80,
    criticalPercentage: currentThreshold?.alerts?.criticalPercentage || 95,
    autoShutoff: currentThreshold?.actions?.autoShutoff || false,
    notifyAdmin: currentThreshold?.actions?.notifyAdmin || true,
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post(`/monitoring/threshold/${associationId}`, {
        enabled: formData.enabled,
        thresholdType: formData.thresholdType,
        thresholdValue: parseFloat(formData.thresholdValue),
        resetPeriod: formData.resetPeriod,
        alerts: {
          enabled: true,
          warningPercentage: parseFloat(formData.warningPercentage),
          criticalPercentage: parseFloat(formData.criticalPercentage),
        },
        actions: {
          autoShutoff: formData.autoShutoff,
          notifyAdmin: formData.notifyAdmin,
        },
      });

      alert('Seuil configuré avec succès');
      onSuccess();
    } catch (error) {
      alert(error.response?.data?.message || 'Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="⚙️ Configuration du seuil de consommation">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Activer/Désactiver */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="w-4 h-4 text-blue-600"
            />
            <label className="text-sm font-medium text-gray-700">
              Activer le contrôle de consommation
            </label>
          </div>
        </div>

        {formData.enabled && (
          <>
            {/* Type de seuil */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de seuil
              </label>
              <select
                value={formData.thresholdType}
                onChange={(e) => setFormData({ ...formData, thresholdType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
              >
                <option value="kwh">Consommation (kWh)</option>
                <option value="fcfa">Coût (FCFA)</option>
              </select>
            </div>

            {/* Valeur du seuil */}
            <Input
              label={`Valeur du seuil (${formData.thresholdType === 'kwh' ? 'kWh' : 'FCFA'})`}
              type="number"
              value={formData.thresholdValue}
              onChange={(e) => setFormData({ ...formData, thresholdValue: e.target.value })}
              min="0"
              step={formData.thresholdType === 'kwh' ? '0.1' : '1'}
              required
            />

            {/* Période de réinitialisation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Période de réinitialisation
              </label>
              <select
                value={formData.resetPeriod}
                onChange={(e) => setFormData({ ...formData, resetPeriod: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-primary"
              >
                <option value="daily">Quotidienne</option>
                <option value="weekly">Hebdomadaire</option>
                <option value="monthly">Mensuelle</option>
              </select>
            </div>

            {/* Alertes */}
            <div className="border border-gray-300 rounded-lg p-4">
              <h3 className="font-bold mb-3">🔔 Alertes</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Alerte d'avertissement (%)"
                  type="number"
                  value={formData.warningPercentage}
                  onChange={(e) => setFormData({ ...formData, warningPercentage: e.target.value })}
                  min="0"
                  max="100"
                />

                <Input
                  label="Alerte critique (%)"
                  type="number"
                  value={formData.criticalPercentage}
                  onChange={(e) => setFormData({ ...formData, criticalPercentage: e.target.value })}
                  min="0"
                  max="100"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="border border-gray-300 rounded-lg p-4">
              <h3 className="font-bold mb-3">⚡ Actions automatiques</h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.autoShutoff}
                    onChange={(e) => setFormData({ ...formData, autoShutoff: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label className="text-sm text-gray-700">
                    Éteindre automatiquement le switch si seuil dépassé
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.notifyAdmin}
                    onChange={(e) => setFormData({ ...formData, notifyAdmin: e.target.checked })}
                    className="w-4 h-4 text-blue-600"
                  />
                  <label className="text-sm text-gray-700">
                    Notifier l'administrateur
                  </label>
                </div>
              </div>

              {formData.autoShutoff && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs text-red-700">
                    ⚠️ Attention : Le switch sera automatiquement éteint lorsque le seuil sera atteint.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Boutons */}
        <div className="flex space-x-3 pt-4">
          <Button type="submit" disabled={loading} className="flex-1">
            {loading ? 'Enregistrement...' : '💾 Enregistrer'}
          </Button>
          <Button variant="secondary" onClick={onClose} className="flex-1">
            Annuler
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ThresholdConfig;